/**
 * Splits game text into the pieces the Text component renders: plain text,
 * keywords, status effects and inline icons.
 *
 * Kept free of Svelte and of the status effect registry so it can be tested
 * on its own -- the component hands it the words to look for.
 */

export type Token =
  | { type: 'text'; text: string }
  | { type: 'keyword'; text: string; key: string }
  | { type: 'effect'; text: string; key: string }
  | { type: 'icon'; name: string };

export type Vocabulary = {
  /** Keyword names, as keys of the keyword glossary. */
  keywords: Array<string>;
  /** Status effect types, as registered. */
  effects: Array<string>;
  /**
   * Other names a status effect goes by, mapped to its type. Poison is
   * registered as 'poisoned', so without this "Poison" would never match.
   */
  aliases?: Record<string, string>;
};

export type Tokeniser = (text: string) => Array<Token>;

function escape(term: string) {
  return term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function createTokeniser({ keywords, effects, aliases = {} }: Vocabulary): Tokeniser {
  const keywordSet = new Set(keywords.map((k) => k.toLowerCase()));
  const effectSet = new Set(effects.map((e) => e.toLowerCase()));
  const aliasMap = new Map(
    Object.entries(aliases).map(([name, type]) => [name.toLowerCase(), type]),
  );

  // Longest first, so "stacks" is tried before "stack" and "ammo (fire)"
  // before "ammo".
  const terms = [...new Set([...keywordSet, ...effectSet, ...aliasMap.keys()])]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  // An inline icon written as <name>, or a whole term with an optional
  // plural s. The lookarounds keep "block" from matching inside "blocking"
  // and leave punctuation outside the match.
  const pattern = terms.length
    ? new RegExp(
        `(<([a-z0-9-]+)>)|(?<![A-Za-z0-9])(${terms.map(escape).join('|')})(s?)(?![A-Za-z0-9])`,
        'gi',
      )
    : /(<([a-z0-9-]+)>)/gi;

  function resolve(term: string): { type: 'keyword' | 'effect'; key: string } | null {
    const word = term.toLowerCase();
    // A keyword wins when a word is both, as it always has.
    if (keywordSet.has(word)) return { type: 'keyword', key: word };
    if (effectSet.has(word)) return { type: 'effect', key: word };
    const aliased = aliasMap.get(word);
    if (aliased) return { type: 'effect', key: aliased };
    return null;
  }

  return (text: string) => {
    const tokens: Array<Token> = [];
    if (!text) return tokens;

    const pushText = (piece: string) => {
      if (!piece) return;
      const last = tokens[tokens.length - 1];
      if (last?.type === 'text') last.text += piece;
      else tokens.push({ type: 'text', text: piece });
    };

    let cursor = 0;
    for (const match of text.matchAll(pattern)) {
      const start = match.index ?? 0;
      pushText(text.slice(cursor, start));

      if (match[1]) {
        tokens.push({ type: 'icon', name: match[2].toLowerCase() });
      } else {
        const term = match[3];
        const plural = match[4];
        // A term that itself ends in s, like "stacks", may have matched as
        // "stack" plus a plural; either way the term decides what it is.
        const found = resolve(term + plural) ?? resolve(term);
        if (found) tokens.push({ ...found, text: match[0] });
        else pushText(match[0]);
      }

      cursor = start + match[0].length;
    }
    pushText(text.slice(cursor));

    return tokens;
  };
}
